import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { errorText } from '../../api/errorText';
import { gapText, severityClass } from '../../drift/findingModel';
import { cveRows, policyText } from '../../drift/cveModel';
import { useTableView } from '../../drift/useTableView';
import ConfirmDialog from '../ConfirmDialog';
import ColumnHeads from './ColumnHeads';

const COLUMNS = [
  { key: 'subject', label: 'What', text: (r) => r.subjectName || r.subject },
  { key: 'gap', label: 'Kind', text: (r) => gapText(r.gap) },
  { key: 'severity', label: 'Worst', text: (r) => r.severity, options: true, order: ['critical', 'high', 'medium', 'low', 'info'] },
  { key: 'machines', label: 'Machines', text: (r) => String(r.machines), sort: (r) => r.machines },
  { key: 'policy', label: 'In the software policy', text: (r) => policyText(r.policy), options: true },
];
const WORDS = { update: 'Update', remove: 'Remove' };

// The programs with known vulnerabilities. A CVE proposes no action of its own: each row says whether the program is in the software policy (what the rule
// that applies to its machines says), and, when it is not, offers Update or Remove: that puts the program in the software policy of each machine that has it
// (POST v3/cve/policy). Nothing runs on a machine; the software policy's own findings propose the update or the removal at the next evaluation.
// For All customers there is no policy to read, so the table is only a list.
export default function CveBlock({ block, data, account, scoped, get, post }) {
  const top = useMemo(() => (data && data.top) || [], [data]);
  const [coverage, setCoverage] = useState({ state: scoped ? 'loading' : 'none', items: [], error: '' });
  const [asking, setAsking] = useState(null);                   // { row, action } waiting for the go-ahead
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState(null);                 // { text } or { error }

  const load = useCallback(() => {
    if (!scoped) return Promise.resolve();
    return get('v3/cve/coverage', { account })
      .then((body) => setCoverage({ state: 'ok', items: (body && body.items) || [], error: '' }))
      .catch((ex) => setCoverage({ state: 'error', items: [], error: errorText(ex) }));
  }, [get, account, scoped]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => cveRows(top, scoped && coverage.state === 'ok' ? coverage.items : null), [top, scoped, coverage]);
  const view = useTableView(rows, COLUMNS);

  const set = async () => {
    const { row, action } = asking;
    setBusy(true);
    setOutcome(null);
    try {
      const body = await post('v3/cve/policy', { account, subject: row.subject, action, device: row.policy.open.join(',') });
      const parts = [`${row.subjectName || row.subject} is now ${WORDS[action]} in ${body.changed.length} software polic${body.changed.length === 1 ? 'y' : 'ies'}.`];
      if (body.noPolicy.length) parts.push(`No customer software policy applies to ${body.noPolicy.join(', ')}, so nothing was changed for ${body.noPolicy.length === 1 ? 'it' : 'them'}.`);
      parts.push('Nothing runs until the software policy is evaluated.');
      setOutcome({ text: parts.join(' ') });
      await load();
    } catch (ex) {
      setOutcome({ error: `${row.subjectName || row.subject} was not changed: ${errorText(ex)}` });
    } finally {
      setBusy(false);
      setAsking(null);
    }
  };

  return (
    <div className="card" role="region" aria-label={block.title}>
      <div className="card-head">
        <div><h3>{block.title}</h3><div className="sub">{block.hint}</div></div>
        <Link className="btn btn-sm" to={`/v3/findings?${scoped ? `account=${encodeURIComponent(account)}&` : ''}capability=${block.key}`}>Open the findings</Link>
      </div>
      <div className="card-body">
        <div className="sub block-line">
          {data.findings} open finding{data.findings === 1 ? '' : 's'} on {data.machines} machine{data.machines === 1 ? '' : 's'}
          {data.worst && <> , worst <span className={`pill ${severityClass(data.worst)}`}>{data.worst}</span></>}
          {data.needsPerson > 0 && `, ${data.needsPerson} need a person`}.
        </div>
      </div>
      {outcome && outcome.text && <div className="notice block-note" role="status">{outcome.text}</div>}
      {outcome && outcome.error && <div className="field-error" role="alert">{outcome.error}</div>}
      {coverage.state === 'error' && <div className="field-error" role="alert">{`The software policy could not be read: ${coverage.error}`}</div>}
      {top.length === 0 ? <div className="empty">Nothing is open.</div> : (
        <div className="table-wrap">
          <table className="open-rows" aria-label={`${block.title} subjects`}>
            <thead><ColumnHeads view={view} tail={scoped ? 1 : 0} tailWidth={170} /></thead>
            <tbody>
              {view.rows.length === 0 && <tr><td colSpan={COLUMNS.length + (scoped ? 1 : 0)} className="empty">Nothing matches the column filters.</td></tr>}
              {view.rows.map((r) => (
                <tr key={`${r.capability}|${r.subject}`}>
                  <td className="strong">{r.subjectName || r.subject}</td><td>{gapText(r.gap)}</td>
                  <td><span className={`pill ${severityClass(r.severity)}`}>{r.severity}</span></td><td>{r.machines}</td>
                  <td>{r.policy ? <span className={`pill pill-plain ${r.policy.state === 'in' ? 'pill-green' : r.policy.state === 'partly' ? 'pill-amber' : 'pill-gray'}`}>{policyText(r.policy)}</span> : <span className="sub">{coverage.state === 'loading' ? 'Loading...' : ''}</span>}</td>
                  {scoped && (
                    <td className="row-actions-inline">
                      {r.policy && r.policy.key && r.policy.open.length > 0 && Object.keys(WORDS).map((action) => (
                        <button key={action} type="button" className="btn btn-sm" disabled={busy} aria-label={`Set ${r.subjectName || r.subject} to ${WORDS[action]} in the software policy`} onClick={() => setAsking({ row: r, action })}>{WORDS[action]}</button>
                      ))}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {asking && (
        <ConfirmDialog title={`Set ${asking.row.subjectName || asking.row.subject} to ${WORDS[asking.action]}?`} confirmLabel={WORDS[asking.action]} busy={busy} onCancel={() => setAsking(null)} onConfirm={set}>
          <p>{`Adds a rule to ${asking.action === 'remove' ? 'remove' : 'update'} ${asking.row.subjectName || asking.row.subject} to the customer software policy of ${asking.row.policy.open.length} machine${asking.row.policy.open.length === 1 ? '' : 's'}: ${asking.row.policy.open.join(', ')}.`}</p>
          <p>Nothing runs on a machine now. When the software policy is evaluated its findings propose the {asking.action === 'remove' ? 'removal' : 'update'}, and you approve them as usual.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}
